/**
 * Variant Normalizer for Shopify Theme
 * 
 * Universal solution that ensures variant selection always resolves to a valid variant.
 * Works with any product structure (1, 2, or 3 options) and automatically corrects
 * invalid combinations while preserving user intent.
 */

class VariantNormalizer {
    constructor() {
        this.productCache = new Map(); // Cache product data per URL
        this.isAdjusting = false; // Guard against recursion
        this.wrappedInstances = new WeakSet(); // Track wrapped instances

        // Wait for custom elements to be defined
        this.init();
    }

    async init() {
        // Wait for product-info custom element to be defined
        await customElements.whenDefined('product-info');

        // Initialize for existing instances
        this.initializeForExistingProducts();

        // Listen for new instances (AJAX swaps)
        document.addEventListener('product-info:loaded', this.handleProductInfoLoaded.bind(this));

        console.log('VariantNormalizer: Initialized with Pub/Sub integration strategy');
    }

    initializeForExistingProducts() {
        const productInfoElements = document.querySelectorAll('product-info');
        productInfoElements.forEach(element => {
            this.setupVariantGuard(element);
        });
    }

    handleProductInfoLoaded(event) {
        const productInfo = event.target;
        this.setupVariantGuard(productInfo);
    }

    setupVariantGuard(productInfo) {
        // Prevent double-setup
        if (this.wrappedInstances.has(productInfo)) return;

        const variantSelects = productInfo.querySelector('variant-selects');
        if (!variantSelects) {
            console.log('VariantNormalizer: No variant-selects found, skipping');
            return;
        }

        // Add capture-phase listener to intercept changes before they bubble up
        variantSelects.addEventListener('change', this.handleOptionChangeCapture.bind(this), { capture: true });

        // Mark as set up
        this.wrappedInstances.add(productInfo);
        console.log('VariantNormalizer: Variant guard set up for variant-selects');
    }

    async handleOptionChangeCapture(event) {
        // Only handle radio button and select changes
        if (!event.target.matches('input[type="radio"], select')) return;

        // Prevent infinite loops
        if (this.isAdjusting) return;

        try {
            this.isAdjusting = true;

            const target = event.target;
            const value = target.value;
            console.log('VariantNormalizer: Intercepting option change for:', value);

            // Get the last changed option index
            const lastChangedIndex = this.getLastChangedOptionIndex(target);
            console.log('VariantNormalizer: Last changed option index:', lastChangedIndex);

            // Get product info element
            const productInfo = target.closest('product-info');
            if (!productInfo) return;

            // Get current selection and normalize it
            const normalizedSelection = await this.normalizeVariantSelection(productInfo, lastChangedIndex);

            if (normalizedSelection) {
                console.log('VariantNormalizer: Normalized selection:', normalizedSelection);

                // Update UI to match normalized selection
                this.syncUIToSelection(productInfo, normalizedSelection);

                // Stop the original event from bubbling
                event.stopImmediatePropagation();

                // Publish normalized variant data through theme's Pub/Sub system
                this.publishNormalizedVariant(productInfo, target, normalizedSelection);

                console.log('VariantNormalizer: ✅ Event intercepted and normalized variant published');
            } else {
                console.log('VariantNormalizer: No valid variant found, letting event proceed normally');
            }

        } catch (error) {
            console.error('VariantNormalizer capture error:', error);
        } finally {
            this.isAdjusting = false;
        }
    }

    getLastChangedOptionIndex(target) {
        if (!target) return 0;

        const variantSelects = target.closest('variant-selects');
        if (!variantSelects) return 0;

        const optionGroups = variantSelects.querySelectorAll('fieldset, .product-form__input--dropdown');

        for (let i = 0; i < optionGroups.length; i++) {
            if (optionGroups[i].contains(target)) {
                return i;
            }
        }

        return 0;
    }

    async normalizeVariantSelection(productInfo, lastChangedIndex) {
        // Get product handle
        const productHandle = this.extractProductHandle(productInfo.dataset.url);
        if (!productHandle) return null;

        // Get or fetch product data
        let productData = this.productCache.get(productHandle);
        if (!productData) {
            try {
                const response = await fetch(`/products/${productHandle}.js`);
                productData = await response.json();
                this.productCache.set(productHandle, productData);
                console.log('VariantNormalizer: Cached product data for:', productHandle);
            } catch (error) {
                console.error('VariantNormalizer: Failed to fetch product data:', error);
                return null;
            }
        }

        // Get current UI selection
        const currentSelection = this.getCurrentUISelection(productInfo);
        console.log('VariantNormalizer: Current UI selection:', currentSelection);

        // Find best matching variant
        const bestVariant = this.findBestMatchingVariant(productData.variants, currentSelection, lastChangedIndex);

        if (bestVariant) {
            console.log('VariantNormalizer: Best matching variant:', bestVariant.title);
            return bestVariant.options;
        }

        return null;
    }

    extractProductHandle(url) {
        if (!url) return null;
        const match = url.match(/\/products\/([^\/\?]+)/);
        return match ? match[1] : null;
    }

    getCurrentUISelection(productInfo) {
        const variantSelects = productInfo.querySelector('variant-selects');
        if (!variantSelects) return [];

        const optionGroups = variantSelects.querySelectorAll('fieldset, .product-form__input--dropdown');
        const selection = [];

        optionGroups.forEach((group, index) => {
            const checkedInput = group.querySelector('input[type="radio"]:checked, select option[selected]');
            const value = checkedInput ? checkedInput.value.trim() : null;
            selection[index] = value;
        });

        return selection.filter(Boolean);
    }

    findBestMatchingVariant(variants, currentSelection, lastChangedIndex) {
        if (!variants || !currentSelection.length) return null;

        console.log('VariantNormalizer: Finding best variant for selection:', currentSelection, 'lastChangedIndex:', lastChangedIndex);

        let bestVariant = null;
        let bestScore = -1;

        for (const variant of variants) {
            if (!variant.options) continue;

            const score = this.calculateVariantScore(variant, currentSelection, lastChangedIndex);
            console.log(`VariantNormalizer: Variant "${variant.title}" score:`, score);

            if (score > bestScore) {
                bestScore = score;
                bestVariant = variant;
            }
        }

        console.log('VariantNormalizer: Best variant selected:', bestVariant?.title, 'with score:', bestScore);
        return bestVariant;
    }

    calculateVariantScore(variant, currentSelection, lastChangedIndex) {
        let score = 0;

        // Bonus for available variants
        if (variant.available) {
            score += 1000;
        }

        // Bonus for matching the last changed option (user intent)
        if (lastChangedIndex >= 0 && lastChangedIndex < variant.options.length) {
            const lastChangedValue = currentSelection[lastChangedIndex];
            if (lastChangedValue && variant.options[lastChangedIndex] === lastChangedValue) {
                score += 500;
            }
        }

        // Score based on option matches with weighted priorities
        // Earlier options (e.g., Width) get higher weight than later ones (e.g., Length)
        const weights = [4, 2, 1]; // For 3 options: Width=4, Height=2, Length=1

        variant.options.forEach((optionValue, index) => {
            const currentValue = currentSelection[index];
            if (currentValue && optionValue === currentValue) {
                const weight = weights[index] || 1;
                score += weight * 100;
            }
        });

        return score;
    }

    syncUIToSelection(productInfo, normalizedSelection) {
        const variantSelects = productInfo.querySelector('variant-selects');
        if (!variantSelects || !normalizedSelection) return;

        console.log('VariantNormalizer: Syncing UI to selection:', normalizedSelection);

        const optionGroups = variantSelects.querySelectorAll('fieldset, .product-form__input--dropdown');

        normalizedSelection.forEach((optionValue, index) => {
            const optionGroup = optionGroups[index];
            if (!optionGroup) return;

            const input = optionGroup.querySelector(`input[value="${optionValue}"], option[value="${optionValue}"]`);
            if (!input) return;

            if (input.type === 'radio') {
                if (!input.checked) {
                    input.checked = true;
                    console.log(`VariantNormalizer: Set radio button ${index} to "${optionValue}"`);
                }
            } else if (input.tagName === 'OPTION') {
                const select = input.closest('select');
                if (select && select.value !== optionValue) {
                    select.value = optionValue;
                    console.log(`VariantNormalizer: Set select ${index} to "${optionValue}"`);
                }
            }
        });

        console.log('VariantNormalizer: ✅ UI synced to normalized selection');
    }

    publishNormalizedVariant(productInfo, target, normalizedSelection) {
        // Find the complete variant object that matches our normalized options
        const variant = this.findVariantByOptions(normalizedSelection);
        if (!variant) {
            console.log('VariantNormalizer: No variant found for normalized options, cannot publish');
            return;
        }

        console.log('VariantNormalizer: Publishing normalized variant:', variant.title);

        // Publish through the theme's Pub/Sub system using the exact same event structure
        // that the theme expects for variant updates
        if (window.publish) {
            window.publish('option-value-selection-change', {
                event: { target: target },
                target: target,
                selectedOptionValues: normalizedSelection
            });

            console.log('VariantNormalizer: ✅ Normalized variant published through theme Pub/Sub');
        } else {
            console.log('VariantNormalizer: ❌ Theme Pub/Sub system not available');
        }
    }

    findVariantByOptions(normalizedOptions) {
        // Find the variant object that matches our normalized options
        const productInfo = document.querySelector('product-info');
        if (!productInfo) return null;

        const productHandle = this.extractProductHandle(productInfo.dataset.url);
        if (!productHandle) return null;

        const productData = this.productCache.get(productHandle);
        if (!productData) return null;

        for (const variant of productData.variants) {
            if (variant.options &&
                variant.options.length === normalizedOptions.length &&
                variant.options.every((option, index) => option === normalizedOptions[index])) {
                return variant;
            }
        }

        return null;
    }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new VariantNormalizer());
} else {
    new VariantNormalizer();
}
